import { Component, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { UniversalSearchModel, UniversalSearchModelType } from 'src/app/core/models/universal-search.model';
import { AuthenticationService, UserService } from 'src/app/core/services';
import { IntentSettingsVerticalTabParam } from 'src/app/modals/intent-settings/constants';
import { VerticalMenuTabsEnum } from 'src/app/modals/intent-settings/enums/vertical-menu-tabs.enum';
import { ModalNavigationEnum } from 'src/app/shared/constants';
import { IsLoading, LoadingWrapper } from 'src/app/shared/decorators';
import { AccountIntentTopic, IntentAgentDashboardListDto, IntentCardSummaryDetail, IntentSurgedCompaniesDto, IntenttCompaniesSurgesCount, IntentWeeklySummaryDto, WeeklySummary } from 'src/app/shared/dto';
import { AddonsIdentifiersEnum, IntentSourceEnum, REQUEST_TYPE } from 'src/app/shared/enums';
import { AccountService } from 'src/app/shared/services';
import { DashboardApiService, IntentAutomationApiService } from 'src/app/shared/services/api';
import { cloneDeep } from 'lodash-es';
import { DataTableColumn } from 'src/app/shared/components';

const LOADER_SURGE_COUNT: string = 'surge-count';
const LOADER_INTENT_AGENT: string = 'inent-agent';
const LOADER_RUN_AUTOMATION: string = 'run-automations';
const LOADER_SURGED_COMPANIES: string = 'surged-companies';
const LOADER_WEEKLY_SUMMARY: string = 'weekly-summary';
const LOADER_MONTHLY_SUMMARY: string = 'monthly-summary';
const LOADER_MONTHLY_COMBINED_SUMMARY: string = 'monthly-combined-summary'

@UntilDestroy()
@Component({
    selector: 'app-intent-card',
    templateUrl: './intent-card.component.html',
    styleUrls: ['./intent-card.component.scss', '../../common-reporting-dashboard.scss'],
    standalone: false
})
export class IntentCardComponent {
    @Output() searchTrigger = new EventEmitter();
    @Output() saveSearchTrigger = new EventEmitter();

    @IsLoading(LOADER_SURGE_COUNT) isSurgeCountLoading$: Observable<boolean>;
    @IsLoading(LOADER_INTENT_AGENT) isIntentAgentLoading$: Observable<boolean>;
    @IsLoading(LOADER_RUN_AUTOMATION) isRunAutomationLoading$: Observable<boolean>;
    @IsLoading(LOADER_SURGED_COMPANIES) isSurgedCompaniesLoading$: Observable<boolean>;
    @IsLoading(LOADER_WEEKLY_SUMMARY) isWeeklySummaryLoading$: Observable<boolean>;
    @IsLoading(LOADER_MONTHLY_SUMMARY) isMonthlySummaryLoading$: Observable<boolean>;
    @IsLoading(LOADER_MONTHLY_COMBINED_SUMMARY) isMonthlyCombinedSummary$: Observable<boolean>;

    selectedOption: IntentAgentDashboardListDto | null = null;
    chartData: Record<string, number[]> = {};
    barChartData: Number[]=[];
    chartLabels: string[];
    intentCardSummaryDetail: IntentCardSummaryDetail;
    surgeCount$: Observable<Record<string, number>>;
    agentSurgedData$: Observable<IntentSurgedCompaniesDto | null>;
    isShowChart: boolean = true;
    isWeeklyAgentsNotRun: boolean =false;
    isAllAgentsEmailOnly: boolean = false;
    isAllAgentsExportOnly: boolean = false;
    exportColumns: DataTableColumn[] = [
        {
            dataField: 'displayDate',
            caption: 'Date',
            cellTemplate: 'dateTemplate',
            width: '40%',
            cellClasses: 'border-b-0 pb-1 px-4',
            headerCellClasses: 'py-1',
            dateFormat: 'MM/dd/yy'
        },
        {
            dataField: 'total_company_count',
            caption: 'Companies Exported',
            cellTemplate: 'numberTemplate',
            width: '30%',
            allowSorting: false,
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1 text-center',
        },
        {
            dataField: 'total_contact_count',
            caption: 'Contacts Exported',
            cellTemplate: 'numberTemplate',
            width: '30%',
            allowSorting: false,
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1 text-center',
        },
    ];
    reportColumns: DataTableColumn[] = [
        {
            dataField: 'displayDate',
            caption: 'Date',
            cellTemplate: 'dateTemplate',
            width: '40%',
            cellClasses: 'border-b-0 pb-1 px-4',
            headerCellClasses: 'py-1',
            dateFormat: 'MM/dd/yy'
        },
        {
            dataField: 'total_company_count',
            caption: 'Surging Companies',
            cellTemplate: 'numberTemplate',
            width: '30%',
            allowSorting: false,
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1 text-center',
        },
    ];
    combinedColumns: DataTableColumn[] = [
        {
            dataField: 'displayDate',
            caption: 'Date',
            cellTemplate: 'dateTemplate',
            width: '30%',
            cellClasses: 'border-b-0 pb-1 px-4',
            headerCellClasses: 'py-1',
            dateFormat: 'MM/dd/yy'
        },
        {
            dataField: 'total_company_count',
            caption: 'COMPANIES EXPORTED',
            cellTemplate: 'numberTemplate',
            width: '20%',
            allowSorting: false,
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1 text-center',

        },
        {
            dataField: 'total_contact_count',
            caption: 'CONTACTS EXPORTED',
            cellTemplate: 'numberTemplate',
            width: '20%',
            allowSorting: false,
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1 text-center',
        },
        {
            dataField: 'all_companies_count',
            caption: 'ALL COMPANIES',
            cellTemplate: 'numberTemplate',
            width: '20%',
            allowSorting: false,
            cellClasses: 'border-b-0 pb-1 px-4 text-center pr-6',
            headerCellClasses: 'py-1 text-center',
        },
    ];
    intentTableData:WeeklySummary[];

    readonly isIntentLoading$: Observable<boolean>;
    readonly topicsPredictiveIntent$: Observable<AccountIntentTopic[]>;
    readonly topicsBombora$: Observable<AccountIntentTopic[]>;
    readonly intentAgentsSetup$: Observable<IntentAgentDashboardListDto[]>;
    readonly isAdmin$: Observable<boolean>;
    readonly adminEmailIds$: Observable<string>;


    private agentSurgedDataSubject = new BehaviorSubject<number | null>(null);
    private reportId: string = null;

    constructor(private readonly router: Router,
        private readonly accountService: AccountService,
        private readonly dashboardApiService: DashboardApiService,
        private readonly intentAutomationApiService: IntentAutomationApiService,
        private readonly userService: UserService,
        private readonly toaster: ToastrService,
        private readonly authenticationService: AuthenticationService,
    ) {
        this.isIntentLoading$ = combineLatest([
            this.isIntentAgentLoading$,
            this.isRunAutomationLoading$,
            this.isSurgedCompaniesLoading$,
            this.isWeeklySummaryLoading$,
            this.isMonthlySummaryLoading$
        ]).pipe(
            map((loaders: boolean[]) => loaders.some((loader: boolean) => loader))
        );
        const topics$: Observable<AccountIntentTopic[]> = this.accountService.getAccountIntentTopics().pipe(shareReplay(1));

        this.topicsPredictiveIntent$ = topics$.pipe(
            map((topics: AccountIntentTopic[]) => this.accountService.hasPlans([AddonsIdentifiersEnum.SALESINTELINTENT]) ? topics?.filter(({ intent_source }) => intent_source === IntentSourceEnum.SALES_INTEL) : [])
        );

        this.topicsBombora$ = topics$.pipe(
            map((topics: AccountIntentTopic[]) => this.accountService.hasPlans([AddonsIdentifiersEnum.BOMBORA_INTENT]) ? topics?.filter(({ intent_source }) => intent_source === IntentSourceEnum.BOMBORA) : [])
        );

        this.intentAgentsSetup$ = this.getIntentAgents().pipe(catchError(() => of([])), shareReplay(1));
        this.isAdmin$ = this.userService.listenToUserChangesByKey('is_admin');
        this.adminEmailIds$ = this.dashboardApiService.getAdminEmails().pipe(map((adminEmailIds: string[]) => adminEmailIds.join(",")))
        this.setCompaniesSurgesCount();
        this.agentSurgedData$ = this.agentSurgedDataSubject.pipe(
            switchMap((id: number | null) => id ? this.getSurgedCompanies(id).pipe(
                tap(data => this.reportId = data?.report_id || null)
            ) : of(null))
        );
        this.getIntentMonthlyCombinedSummaryData();
    }

    onOpenIntentSettings() {
        this.router.navigate(['/', { outlets: { modal: [ModalNavigationEnum.INTENT_SETTINGS] } }], {
            queryParams: {
                [IntentSettingsVerticalTabParam]: VerticalMenuTabsEnum.GENERAL,
            },
            queryParamsHandling: 'merge'
        })
    }

    navigateToSearch(searchModelData: UniversalSearchModelType) {
        this.searchTrigger.emit(searchModelData);
    }

    updateSelectedOption(value: IntentAgentDashboardListDto | null) {
        this.selectedOption = value;
        this.reportId = null;
        if(this.selectedOption) {
            if(this.selectedOption?.email_only) {
                this.agentSurgedDataSubject.next(value?.id || null);
                this.getIntentMonthlyReportSummaryData(value?.id)
            } else {
                this.getIntentWeeklySummaryData(value?.id);
            }
        } else {
            this.getIntentMonthlyCombinedSummaryData();
        }
    }

    getIntentMonthlyCombinedSummaryData() {
        this.getIntentMonthlyCombinedSummary().pipe(untilDestroyed(this)).subscribe((res: IntentWeeklySummaryDto) => {
            this.isAllAgentsEmailOnly = res.is_email_only;
            this.isAllAgentsExportOnly =  res.is_export_only;
            if(!res.is_email_only && !res.is_export_only) {
                this.isWeeklyAgentsNotRun = res.weekly_summary.reduce((sum, entry) => sum + entry.total_company_count + entry.total_contact_count + entry.all_companies_count, 0) === 0;
                this.intentTableData = res.weekly_summary.map((data: WeeklySummary) => ({...data, "displayDate": moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YYYY').toString(), total_company_count: data.total_company_count || 0, total_contact_count: data.total_contact_count || 0, all_companies_count: data.all_companies_count || 0}))
                this.chartLabels = [];
                this.chartData = {};
                this.chartData = {...this.chartData, "Companies Exported": [], "Contacts Exported":[], "All Companies":[]};
                res.weekly_summary.forEach((data: WeeklySummary) => {
                    const weekLabel = moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YY').toString();
                    this.chartLabels.push(weekLabel);
                    this.chartData["Companies Exported"].push(data.total_company_count || 0);
                    this.chartData["Contacts Exported"].push(data.total_contact_count || 0);
                    this.chartData["All Companies"].push(data.all_companies_count || 0);
                })
            } else if(res.is_export_only) {
                this.processExportData(res);
            } else if(res.is_email_only) {
                this.processEmailOnlyData(res, 'all_companies_count');
            }
        });
    }

    getIntentWeeklySummaryData(automationId?: number): void {
        this.getIntentWeeklySummary(automationId).pipe(untilDestroyed(this)).subscribe((res: IntentWeeklySummaryDto) => {
            this.processExportData(res);
        })
    }

    getIntentMonthlyReportSummaryData(automationId: number): void {
        this.getIntentMonthlyReportSummary(automationId).pipe(untilDestroyed(this)).subscribe((res: IntentWeeklySummaryDto) => {
            this.processEmailOnlyData(res, 'total_company_count');
        })
    }

    runAgent() {
        this.runIntentAutomation([this.selectedOption.id]).pipe(untilDestroyed(this)).subscribe(() => this.toaster.success("Success! Your Intent Automation has run."));
    }

    showReport() {
        if (this.selectedOption?.email_only && this.reportId) {
            this.router.navigate([], {
                queryParams: { intentId: this.reportId }
            });
        } else {
            const paramsToUpdate = {
                intent_maximum_score: this.intentCardSummaryDetail.score_threshold_max,
                intent_minimum_score: this.intentCardSummaryDetail.score_threshold_min,
                ...this.setTopics()
            }
            if (!this.intentCardSummaryDetail.universal) {
                this.saveSearchTrigger.emit({paramsToUpdate, saved_search_id: this.intentCardSummaryDetail.saved_search_id});
            } else {
                const searchModelData: UniversalSearchModelType = {
                    ...cloneDeep(UniversalSearchModel),
                    request_type: REQUEST_TYPE.COMPANIES,
                    page: 1,
                    verified: 1,
                    tiers: this.authenticationService.getHumanVerifiedTiers().toString(),
                    ...paramsToUpdate
                }
                this.navigateToSearch(searchModelData);
            }
        }
    }

    private processExportData(res: IntentWeeklySummaryDto): void {
        this.isWeeklyAgentsNotRun = res.weekly_summary.reduce((sum, entry) => sum + entry.total_company_count + entry.total_contact_count, 0) === 0;
        this.intentCardSummaryDetail = cloneDeep(res.intent_card_summary_detail);
        this.intentTableData = res.weekly_summary.map((data: WeeklySummary) => ({...data, "displayDate": moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YYYY').toString(), total_company_count: data.total_company_count || 0, total_contact_count: data.total_contact_count || 0}))
        this.chartLabels = [];
        this.chartData = {};
        this.chartData = {...this.chartData, "Companies": [], "Contacts":[]};
        res.weekly_summary.forEach((data: WeeklySummary) => {
            const weekLabel = moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YY').toString();
            this.chartLabels.push(weekLabel);
            this.chartData["Companies"].push(data.total_company_count);
            this.chartData["Contacts"].push(data.total_contact_count);
        })
    }

    private processEmailOnlyData(res: IntentWeeklySummaryDto, countKey: string): void {
        this.isWeeklyAgentsNotRun = res.weekly_summary.reduce((sum, entry) => sum + entry[countKey], 0) === 0;
        this.intentCardSummaryDetail = cloneDeep(res.intent_card_summary_detail);
        this.intentTableData = res.weekly_summary.map((data: WeeklySummary) => ({ ...data, "displayDate": moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YYYY').toString(),  total_company_count: data[countKey] || 0}))
        this.chartLabels = [];
        this.barChartData = [];
        res.weekly_summary.forEach((data: WeeklySummary) => {
            const weekLabel = moment(data.displayDate, 'YYYY-MM-DD').format('MM/DD/YY').toString();
            this.chartLabels.push(weekLabel);
            this.barChartData.push(data[countKey]);
        })
    }

    private setTopics(): { si_intent_topic_ids: number[] } | { intent_topic_ids: number[] } {
        if (this.intentCardSummaryDetail.intent_type === IntentSourceEnum.SALES_INTEL) {
            return { si_intent_topic_ids: this.intentCardSummaryDetail.topics.map(topic => topic.topic_id) }
        }
        return { intent_topic_ids: this.intentCardSummaryDetail.topics.map(topic => topic.topic_id) }
    }

    private setCompaniesSurgesCount() {
        this.surgeCount$ = this.getCompaniesSurgesCount().pipe(
            map((companiesSurgeResponse: IntenttCompaniesSurgesCount[]) => companiesSurgeResponse.reduce((acc, surge) => {
                if (surge.intentSource && surge.companiesCount !== undefined) {
                    acc[surge.intentSource] = surge.companiesCount;
                }
                return acc;
            }, {} as Record<string, number>)),
            shareReplay(1)
        );
    }

    @LoadingWrapper(LOADER_RUN_AUTOMATION)
    private runIntentAutomation(automationIds: number[]) {
        return this.intentAutomationApiService.runIntentAutomation({ automationIds });
    }

    @LoadingWrapper(LOADER_SURGE_COUNT)
    private getCompaniesSurgesCount(): Observable<IntenttCompaniesSurgesCount[]> {
        return this.dashboardApiService.getCompaniesSurgesCount();
    }

    @LoadingWrapper(LOADER_INTENT_AGENT)
    private getIntentAgents(): Observable<IntentAgentDashboardListDto[]> {
        return this.dashboardApiService.getIntentAgents();
    }

    @LoadingWrapper(LOADER_SURGED_COMPANIES)
    private getSurgedCompanies(automationId: number): Observable<IntentSurgedCompaniesDto> {
        return this.dashboardApiService.getSurgedCompanies(automationId);
    }

    @LoadingWrapper(LOADER_WEEKLY_SUMMARY)
    private getIntentWeeklySummary(automationId: number): Observable<IntentWeeklySummaryDto> {
        return this.dashboardApiService.getIntentWeeklySummary(automationId);
    }

    @LoadingWrapper(LOADER_MONTHLY_SUMMARY)
    private getIntentMonthlyReportSummary(automationId: number): Observable<IntentWeeklySummaryDto> {
        return this.dashboardApiService.getIntentMonthlyReportSummary(automationId);
    }

    @LoadingWrapper(LOADER_MONTHLY_COMBINED_SUMMARY)
    private getIntentMonthlyCombinedSummary() {
        return this.dashboardApiService.getIntentMonthlyCombinedSummary();
    }

}
